import * as React from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const cards = [
  { title: 'Maths', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/maths.avif', path: '/Maths' },
  { title: 'Science', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/science.avif', path: '/Science' },
  { title: 'English', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/english.avif', path: '/English' },
  { title: 'Geography', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/geography.avif', path: '/Geography' },
  { title: 'History', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/history.avif', path: '/History' },
  { title: 'ICT', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/ICT.avif', path: '/ICT' },
  { title: 'Business Studies', image: 'https://websitecontent.ams3.cdn.digitaloceanspaces.com/BusStudiesMenu.png', path: '/BusinessStudies' },
];

const theme = createTheme();

function LessonSelect() {
  const navigate = useNavigate();

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>Choose Your Subject | Rocket Learning Classroom</title>
        <meta
          name="description"
          content="Explore the Rocket Learning Classroom where students can choose from a variety of subjects such as Maths, Science, English, and more. Start your learning journey with our curriculum-aligned lessons and engaging content."
        />
        <meta
          name="keywords"
          content="online learning, Rocket Learning, home education, Maths lessons, Science lessons, English lessons, primary school, high school, curriculum-aligned education"
        />
      </Helmet>
      <CssBaseline />

      {/* Background matches your dashboard gradient */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #4a91e3 0%, #6bb5ff 100%)',
          minHeight: '100vh',
          width: '100%',
        }}
      >
        {/* Hero */}
        <Box sx={{ pt: 8, pb: 6 }}>
          <Container maxWidth="md">
            <Typography variant="h3" align="center" sx={{ color: 'common.white', fontWeight: 800 }} gutterBottom>
              Your Learning Adventure Awaits
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: alpha('#ffffff', 0.9) }} paragraph>
              Choose your subject to step through the doors of knowledge and continue your educational journey!
            </Typography>
            <Typography variant="body1" align="center" sx={{ color: alpha('#ffffff', 0.95) }}>
              Not sure where to begin?{' '}
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/Assessments')}
                sx={{
                  ml: 1,
                  borderColor: alpha('#ffffff', 0.7),
                  color: 'common.white',
                  '&:hover': { borderColor: 'common.white', backgroundColor: alpha('#ffffff', 0.1) },
                }}
              >
                Try an assessment
              </Button>
            </Typography>
          </Container>
        </Box>

        {/* Cards on a rounded panel to echo your dashboard Papers */}
        <Container sx={{ pb: 10 }}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              bgcolor: 'background.paper',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              border: (t) => `1px solid ${alpha(t.palette.common.black, 0.06)}`,
            }}
          >
            <Grid container spacing={3}>
              {cards.map((card) => (
                <Grid item key={card.title} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 24px rgba(0,0,0,0.16)',
                      transition: 'transform 160ms ease, box-shadow 160ms ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 36px rgba(0,0,0,0.22)',
                      },
                      '&:focus-within': {
                        outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.85)}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <CardActionArea
                      component={Link}
                      to={card.path}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <CardMedia
                          component="img"
                          src={card.image}
                          loading="lazy"
                          alt={`${card.title} cover`}
                          sx={{
                            width: '100%',
                            aspectRatio: '16/9',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        {/* Overlay for readability */}
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.45) 100%)',
                          }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            position: 'absolute',
                            left: 16,
                            bottom: 12,
                            color: 'common.white',
                            fontWeight: 800,
                            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                          }}
                        >
                          {card.title}
                        </Typography>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          Curriculum-aligned lessons.
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default LessonSelect;
